import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowUpCircle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  valide: 'bg-green-500', approved: 'bg-green-500',
  pending: 'bg-orange-500', en_attente: 'bg-orange-500',
  annule: 'bg-red-500/20 text-red-400', rejected: 'bg-red-500/20 text-red-400',
};

const PAGE_SIZES = [10, 20, 50, 100, 500];

export default function AdminWithdrawals() {
  const { token, user } = useAuth();
  const { currentTemplate } = useTheme();
  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [offices, setOffices] = useState([]);
  const [filters, setFilters] = useState({
    status: 'pending',
    office_id: '',
    analyst_id: '',
    start_date: '',
    end_date: ''
  });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin_ops' || user?.role === 'bureau';
  const isAnalyst = user?.role === 'analyst';
  const showOfficeFilter = isAdmin || isAnalyst;
  const showAnalystFilter = isAdmin || isAnalyst;
  const [analysts, setAnalysts] = useState([]);

  useEffect(() => {
    if (!showOfficeFilter) return;
    axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setOffices(res.data || []))
      .catch(() => {});
  }, [token, showOfficeFilter]);

  useEffect(() => {
    if (!showAnalystFilter) return;
    axios.get(`${API}/analysts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : res.data?.analysts || [];
        setAnalysts(list);
      })
      .catch(() => {});
  }, [token, showAnalystFilter]);

  const filteredAnalysts = filters.office_id
    ? analysts.filter(a => a.office_id === filters.office_id)
    : analysts;

  const fetchWithdrawals = useCallback(async (p = page, size = pageSize) => {
    setLoading(true);
    try {
      const params = { page: p, limit: size, tx_type: 'retrait' };
      if (filters.status) params.status = filters.status;
      if (filters.office_id) params.office_id = filters.office_id;
      if (filters.analyst_id) params.analyst_id = filters.analyst_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const res = await axios.get(`${API}/transactions-all`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
      setPage(p);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [token, filters, pageSize]);

  useEffect(() => { fetchWithdrawals(1, pageSize); }, [filters, pageSize]);

  const updateStatus = async (tx, newStatus) => {
    try {
      await axios.put(`${API}/transactions/${tx.client_id}/${tx.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newStatus === 'valide' ? 'Retrait validé' : 'Retrait refusé');
      fetchWithdrawals(page, pageSize);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
      if (page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-withdrawals-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Retraits</h1>
            <p className="text-sm text-muted-foreground">{total} retrait{total > 1 ? 's' : ''}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchWithdrawals(1, pageSize)}>
            <RefreshCw className="w-4 h-4 mr-1" /> Rafraîchir
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-card/80">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              {showOfficeFilter && (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Bureau</label>
                  <Select value={filters.office_id || 'all'} onValueChange={(v) => setFilters(p => ({...p, office_id: v === 'all' ? '' : v}))}>
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-office-wd"><SelectValue placeholder="Tous les bureaux" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les bureaux</SelectItem>
                      {offices.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {showAnalystFilter && (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Analyste</label>
                  <Select value={filters.analyst_id || 'all'} onValueChange={(v) => setFilters(p => ({...p, analyst_id: v === 'all' ? '' : v}))}>
                    <SelectTrigger className="w-44 h-8 text-xs" data-testid="filter-analyst-wd"><SelectValue placeholder="Tous les analystes" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les analystes</SelectItem>
                      {filteredAnalysts.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Statut</label>
                <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(p => ({...p, status: v === 'all' ? '' : v}))}>
                  <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Tous" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="valide">Validé</SelectItem>
                    <SelectItem value="annule">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Du</label>
                <Input type="date" value={filters.start_date} onChange={(e) => setFilters(p => ({...p, start_date: e.target.value}))} className="w-36 h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Au</label>
                <Input type="date" value={filters.end_date} onChange={(e) => setFilters(p => ({...p, end_date: e.target.value}))} className="w-36 h-8 text-xs" />
              </div>
              <Button variant="ghost" size="sm" onClick={() => setFilters({ status: 'pending', office_id: '', analyst_id: '', start_date: '', end_date: '' })}>
                Effacer
              </Button>
              <div className="ml-auto">
                <Badge variant="outline">{total} retrait{total > 1 ? 's' : ''}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-card/80">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="withdrawals-table">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-2">Client</th>
                    <th className="text-left py-3 px-2">Description</th>
                    <th className="text-right py-3 px-2">Montant</th>
                    <th className="text-center py-3 px-2">Statut</th>
                    <th className="text-center py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-8"><RefreshCw className="w-5 h-5 animate-spin mx-auto" /></td></tr>
                  ) : transactions.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">Aucun retrait</td></tr>
                  ) : transactions.map(tx => {
                    const isPending = ['pending', 'en_attente', 'en_verification', 'en_cours'].includes(tx.status);
                    return (
                      <tr key={tx.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`withdrawal-${tx.id}`}>
                        <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="py-3 px-2">
                          <Link to={`/admin/clients/${tx.client_id}`} className="hover:underline text-primary text-sm font-medium">
                            {tx.client_name || 'Client'}
                          </Link>
                        </td>
                        <td className="py-3 px-2 text-xs text-muted-foreground">{tx.description || 'Demande de retrait'}</td>
                        <td className="py-3 px-2 text-right font-mono font-semibold text-red-500">
                          -{Math.abs(tx.amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge className={`text-[10px] ${STATUS_COLORS[tx.status] || 'bg-muted'}`}>
                            {tx.status === 'valide' ? 'Validé' : isPending ? 'En attente' : tx.status === 'annule' ? 'Annulé' : tx.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {isPending ? (
                            <div className="flex items-center gap-2 justify-center">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs"
                                onClick={() => updateStatus(tx, 'valide')} data-testid={`approve-${tx.id}`}>
                                Valider
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-500 border-red-500 h-7 text-xs"
                                onClick={() => updateStatus(tx, 'annule')} data-testid={`reject-${tx.id}`}>
                                Refuser
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <p className="text-xs text-muted-foreground">
                  {total} résultat{total > 1 ? 's' : ''} — Page {page}/{totalPages || 1}
                </p>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-7 text-xs" data-testid="page-size-select-wd"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map(s => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">par page</span>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1" data-testid="pagination-controls-wd">
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => fetchWithdrawals(page - 1, pageSize)}>
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  {getPageNumbers().map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className="px-1 text-xs text-muted-foreground">...</span>
                    ) : (
                      <Button
                        key={p}
                        variant={p === page ? 'default' : 'outline'}
                        size="icon"
                        className="h-7 w-7 text-xs"
                        style={p === page ? { backgroundColor: primaryColor } : {}}
                        onClick={() => fetchWithdrawals(p, pageSize)}
                      >
                        {p}
                      </Button>
                    )
                  )}
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => fetchWithdrawals(page + 1, pageSize)}>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
